import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, Loader2, RefreshCw } from 'lucide-react';
import MuscleMap from '../MuscleMap/MuscleMap';
import { workoutExecution } from '../../services/api';
import './planStudio.css';

// Recuperação muscular do atleta, por músculo: quando foi a última vez e quantas séries na última semana. São FACTOS
// lidos dos treinos concluídos. O componente não decide se um músculo está «fatigado» — esse limiar é regra do ginásio,
// e por isso a legenda diz apenas há quanto tempo foi treinado.

/** Escala de calor: mais forte = treinado há menos tempo (por isso ainda «fresco» na memória do treino). */
const typical = (days) => {
  if (days == null) return 0;
  if (days <= 1) return 4;
  if (days <= 3) return 3;
  if (days <= 7) return 2;
  return 1;
};

const phrase = (m) => {
  if (m.daysSince == null) return 'nunca treinado';
  if (m.daysSince === 0) return 'treinado hoje';
  if (m.daysSince === 1) return 'treinado ontem';
  return `treinado há ${m.daysSince} dias`;
};

const LEGEND = [
  { tone: 4, label: 'hoje / ontem' },
  { tone: 3, label: 'há 2-3 dias' },
  { tone: 2, label: 'há 4-7 dias' },
  { tone: 1, label: 'há mais de uma semana' },
];

export default function RecoveryHeatmap({ userId = null, load = (id) => workoutExecution.muscleRecovery(id), compact = false }) {
  const [state, setState] = useState({ status: 'idle', data: null });
  const [face, setFace] = useState('front');
  const [reload, setReload] = useState(0);

  const fetchRecovery = useCallback(async () => {
    setState((s) => ({ status: 'loading', data: s.data }));
    try {
      const res = await load(userId);
      setState({ status: 'ok', data: res?.data || null });
    } catch {
      setState({ status: 'error', data: null });
    }
  }, [load, userId]);

  useEffect(() => { fetchRecovery(); }, [fetchRecovery, reload]);

  const muscles = useMemo(() => state.data?.muscles || [], [state.data]);
  const intensity = useMemo(() => {
    const map = {};
    muscles.forEach((m) => { map[m.muscleId] = typical(m.daysSince); });
    return map;
  }, [muscles]);

  const describe = useCallback((muscleId) => {
    const m = muscles.find((x) => x.muscleId === muscleId);
    if (!m) return null;
    return m.setsLast7Days > 0 ? `${phrase(m)} · ${m.setsLast7Days} séries nos últimos 7 dias` : phrase(m);
  }, [muscles]);

  const trained = muscles.filter((m) => m.daysSince != null).sort((a, b) => (a.daysSince ?? 0) - (b.daysSince ?? 0));
  const untouched = muscles.filter((m) => m.daysSince == null);

  if (state.status === 'error') {
    return (
      <div className="ps-note" data-testid="recovery-error" role="alert">
        Não foi possível carregar a recuperação do atleta.{' '}
        <button type="button" className="ps-chip" onClick={() => setReload((n) => n + 1)}><RefreshCw size={12} aria-hidden="true" /> Tentar de novo</button>
      </div>
    );
  }

  if (state.status === 'loading' && !state.data) {
    return <div className="ps-note" data-testid="recovery-loading" role="status"><Loader2 size={14} className="ps-spin" aria-hidden="true" /> A carregar a recuperação…</div>;
  }

  return (
    <div data-testid="recovery-heatmap">
      <div className="ps-chip-row" style={{ marginBottom: 8 }}>
        <span className="ps-chip-label">Recuperação</span>
        <button type="button" className={`ps-chip ${face === 'front' ? 'is-on' : ''}`} onClick={() => setFace('front')} aria-pressed={face === 'front'}>Frente</button>
        <button type="button" className={`ps-chip ${face === 'back' ? 'is-on' : ''}`} onClick={() => setFace('back')} aria-pressed={face === 'back'}>Costas</button>
        <button type="button" className="ps-chip" onClick={() => setReload((n) => n + 1)} title="Atualizar" aria-label="Atualizar a recuperação"><RefreshCw size={12} aria-hidden="true" /></button>
      </div>

      <MuscleMap
        view={face}
        intensity={intensity}
        describeMuscle={describe}
        height={compact ? 240 : 320}
        legend={LEGEND.map((l) => (
          <span key={l.tone} className="mm-legend-item">
            <span className={`mm-legend-swatch heat-${l.tone}`} style={{ background: ['#fef3c7', '#fcd34d', '#f59e0b', '#b45309'][l.tone - 1] }} aria-hidden="true" />
            {l.label}
          </span>
        ))}
      />

      {muscles.length === 0 ? <p className="ps-note">Ainda não há treinos concluídos para calcular a recuperação.</p> : null}

      {trained.length > 0 ? (
        <ul className="ps-muscle-list" data-testid="recovery-list">
          {trained.slice(0, compact ? 4 : 8).map((m) => (
            <li key={m.muscleId}>
              <span className="ps-muscle-name">{m.name}</span>
              <span className="ps-muscle-days"><Activity size={12} aria-hidden="true" /> {phrase(m)}</span>
              {m.setsLast7Days > 0 ? <span className="ps-muscle-sets">{m.setsLast7Days} séries/7 dias</span> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {untouched.length > 0 ? (
        <p className="ps-note" data-testid="recovery-untouched">
          Nunca treinados (ou sem ligação à biblioteca): {untouched.slice(0, 6).map((m) => m.name).join(', ')}
          {untouched.length > 6 ? ` e mais ${untouched.length - 6}` : ''}.
        </p>
      ) : null}

      <p className="ps-note">
        Só se mostra há quanto tempo cada músculo foi treinado. O juízo «ainda está fatigado» precisa de um limiar definido
        pelo ginásio — não é o código que o inventa.
      </p>
    </div>
  );
}
